import { readAccessToken, readSessionRole, resolveTabLogoutAction } from '@/auth/tabStorage';
import createPermission from '@/permission';
import * as auth from '@/utils/auth';
import { readEntryRole, saveReloginContext } from '@/utils/reloginContext';
import { pickPreferredRole } from '@/utils/role';

import { whiteList as permissionWhiteList } from './permission';
import addRouterStateHandler from './router/routerStateHandler';

// Mount a window storage-event listener so this tab reacts when another tab
// clears the admin's localStorage session (real logout). If THIS tab is
// currently impersonating, it has its own sessionStorage tokens and stays up.
const mountCrossTabLogoutWatcher = (router) => {
  window.addEventListener('storage', (event) => {
    const action = resolveTabLogoutAction(event);
    if (action === 'logout') {
      saveReloginContext({
        url: `${window.location.pathname}${window.location.search}${window.location.hash}`,
        role: auth.readActiveRole(),
      });
      router.replace({ name: 'login' }).catch(() => {});
    }
  });
};

export default async function initializeApp({ store, router, userSettings, config }) {
  // Session-aware token read: prefers sessionStorage imp-tokens for impersonation
  // tabs, falls back to localStorage for normal sessions.
  const hasToken = Boolean(readAccessToken());

  if (!hasToken && permissionWhiteList.indexOf(window.location.pathname) === -1) {
    const entryUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    saveReloginContext({ url: entryUrl });
    store.commit('SET_ENTRY_URL', entryUrl);
    await router.replace({ name: 'login' });
    return;
  }

  if (hasToken) {
    const waitGroup = [];
    waitGroup.push(store.dispatch('fetchGrants'));
    waitGroup.push(store.dispatch('fetchUser'));
    waitGroup.push(userSettings.init());
    await Promise.all(waitGroup);
  } else {
    createPermission({ store, router });
    addRouterStateHandler(router);
    return;
  }

  // Query-param role: ?role=<name> written into the URL by middle-click / new-tab
  // links in the role switcher. Validated against availableRoles; silently ignored
  // if invalid or if availableRoles is empty. Stripped from the URL regardless so
  // subsequent reloads don't re-apply it.
  const availableRoles = store.getters.availableRoles ?? [];
  const urlParams = new URLSearchParams(window.location.search);
  const roleParam = urlParams.get('role');
  let requestedRole = null;
  if (roleParam !== null) {
    urlParams.delete('role');
    const cleanSearch = urlParams.toString();
    const cleanUrl = `${window.location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}${window.location.hash}`;
    history.replaceState(null, '', cleanUrl);
    if (availableRoles.length > 0 && availableRoles.includes(roleParam)) {
      requestedRole = roleParam;
      sessionStorage.setItem('role', roleParam);
    }
  }

  const entryRole = readEntryRole();
  const savedRole = readSessionRole();
  const preferredRole = pickPreferredRole(availableRoles) || availableRoles[0];

  // Relogin context must win over stale role storage: token expiry can leave
  // local/session role at a lower-privilege value such as publisher.
  const restoredRole = [requestedRole, entryRole, savedRole, preferredRole].find(
    (role) => role && availableRoles.includes(role),
  );

  if (restoredRole) {
    store.dispatch('switchRole', restoredRole);
  }

  mountCrossTabLogoutWatcher(router);

  createPermission({ store, router });

  addRouterStateHandler(router);
}
